import { ArrowUpRight, FileCheck2, ShieldCheck } from "lucide-react";

const LOGO = `${import.meta.env.BASE_URL}matterline.svg`;
const REVIEWED_ON = "2026-09-08";

const PRODUCTS = [
  { id: "matterline", name: "Matterline", href: null },
  {
    id: "workbuddy",
    name: "WorkBuddy",
    href: "https://cloud.tencent.cn/product/workbuddy"
  },
  {
    id: "chatgpt-work",
    name: "ChatGPT Work",
    href: "https://learn.chatgpt.com/docs/get-started-with-work"
  },
  {
    id: "hermes-agent",
    name: "Hermes Agent",
    href: "https://hermes-agent.nousresearch.com/docs/"
  }
] as const;

type ProductId = (typeof PRODUCTS)[number]["id"];

const DIMENSIONS = [
  {
    id: "private-deploy",
    tone: "blue",
    label: "私有部署",
    claim: "不止模型能运行，更有整套部署方案",
    boundary: "Matterline 的内网部署按模型、存储与工具外联清单逐项验收。",
    cells: {
      matterline: {
        lead: "从硬件到应用，一体交付。",
        note: "结合企业工作站，为 Agent 推理、知识处理与文生图分别配置服务，验证模型组合和推理参数。员工直接使用企业准备好的 AI 服务。"
      },
      workbuddy: {
        lead: "本地处理文件，仍有服务端参与。",
        note: "官方标准任务说明：原始文件默认在本地处理，但服务端仍处理数据片段。仅安装客户端，不能据此认定 AI 全程在企业内网运行。",
        source: "https://www.codebuddy.cn/docs/workbuddy/Conversation"
      },
      "chatgpt-work": {
        lead: "本地执行，不是离线推理。",
        note: "官方明确：本地任务中的文件片段、提示词或工具结果可能发送至 OpenAI 服务。仅选择本地模式，不能满足模型与数据全内网处理的要求。",
        source: "https://learn.chatgpt.com/docs/enterprise/chatgpt-work-local-security"
      },
      "hermes-agent": {
        lead: "本地模型可用，企业配套仍要集成。",
        note: "可自动管理本机模型与推理环境。但本地推理只是其中一层；企业知识、团队 IM 和业务系统仍需按目标环境完成集成与验证。",
        source: "https://hermes-agent.nousresearch.com/docs/zh-Hans/user-guide/local-models"
      }
    }
  },
  {
    id: "team-context",
    tone: "violet",
    label: "团队协作",
    claim: "不只共享资料，也保留协作的来龙去脉",
    boundary: "共享遵循权限，私密单聊不默认公开；会话同步不等于自动入库。",
    cells: {
      matterline: {
        lead: "讨论、资料与成果，在同一项目中积累。",
        note: "内置团队 IM、会话历史同步、文件空间与知识库。有权限的同事能沿着讨论和引用继续工作，减少重复上传和反复交代背景。"
      },
      workbuddy: {
        lead: "团队资料可共享，标准存储在云端。",
        note: "已有团队空间、协作与权限管理；但官方资料库使用腾讯网盘及 WorkBuddy 云端存储，不等于企业内部自托管的知识与协作服务。",
        source: "https://www.codebuddy.cn/docs/workbuddy/From-Beginner-to-Expert-Guide/Function-Description/Library"
      },
      "chatgpt-work": {
        lead: "能连接资料，跨系统协作仍需整合。",
        note: "通过插件获取 Slack、网盘等系统的信息。采用这一路径，企业仍需维护来源系统及授权，并组织跨系统的资料与工作流程。",
        source: "https://learn.chatgpt.com/docs/get-started-with-work"
      },
      "hermes-agent": {
        lead: "内置记忆隔离，共享需额外接入。",
        note: "内置记忆按配置档隔离，官方不建议多个 Agent 共用同一目录。跨 Agent 共享记忆需接入外部记忆服务，不能靠共用目录变成团队知识库。",
        source: "https://hermes-agent.nousresearch.com/docs/user-guide/features/memory/"
      }
    }
  },
  {
    id: "role-delivery",
    tone: "amber",
    label: "岗位交付",
    claim: "业务流程提前适配，员工不必从零配置",
    boundary: "技术适配由交付团队负责，关键业务判断仍由员工确认。",
    cells: {
      matterline: {
        lead: "按岗位交付数字员工。",
        note: "交付团队接入企业系统、业务规则与流程。非技术人员用业务语言提出需求、补充信息、确认成果，不必配置模型或编写 Skill。"
      },
      workbuddy: {
        lead: "企业 Agent 可创建，配置工作仍要做。",
        note: "官方创建流程包括定义角色规则、选择模型、挂载技能与连接器，再测试响应。预置专家之外，企业专有流程仍需专人适配。",
        source: "https://www.codebuddy.cn/docs/workbuddy/From-Beginner-to-Expert-Guide/Function-Description/CloudAgent"
      },
      "chatgpt-work": {
        lead: "能执行任务，不等于岗位流程已就绪。",
        note: "自然语言与插件能完成多步工作；用于企业专有流程时，仍需准备业务规则、接入所需系统并验证结果。这些适配工作不会因能对话而消失。",
        source: "https://learn.chatgpt.com/docs/get-started-with-work"
      },
      "hermes-agent": {
        lead: "Skill 能复用，系统接入仍需配置。",
        note: "技能可以创建与复用，但企业系统接入仍涉及工具服务、认证与权限配置。生成 Skill 不等于完成岗位流程的测试与交付。",
        source: "https://hermes-agent.nousresearch.com/docs/user-guide/features/mcp/"
      }
    }
  }
] as const satisfies ReadonlyArray<{
  id: string;
  tone: "blue" | "violet" | "amber";
  label: string;
  claim: string;
  boundary: string;
  cells: Record<ProductId, { lead: string; note: string; source?: string }>;
}>;

function ProductName({
  product,
  asLink
}: {
  product: (typeof PRODUCTS)[number];
  asLink: boolean;
}) {
  const mark =
    product.id === "matterline" ? (
      <img src={LOGO} alt="" width={18} height={18} />
    ) : null;

  if (asLink && product.href) {
    return (
      <a href={product.href} target="_blank" rel="noreferrer" aria-label={`${product.name} 官方资料`}>
        {mark}
        {product.name}
        <ArrowUpRight size={14} aria-hidden="true" />
      </a>
    );
  }

  return (
    <span>
      {mark}
      {product.name}
    </span>
  );
}

export function AgentComparison() {
  return (
    <section className="agent-comparison" id="comparison" aria-labelledby="comparison-heading">
      <div className="comparison-inner">
        <header className="comparison-heading">
          <h2 id="comparison-heading">
            同样是 Agent，
            <br />
            企业拿到的有什么不同？
          </h2>
          <p>
            会执行任务，不等于完成企业落地。逐项看清：哪些能力已经具备，哪些部署、整合与岗位适配工作还需要企业承担。
          </p>
        </header>

        <div className="comparison-board" role="region" aria-label="按三个交付维度比较四个方案">
          <div className="comparison-products-head">
            {PRODUCTS.map((product) => (
              <div
                className="comparison-product-head"
                data-product={product.id}
                key={product.id}
              >
                <ProductName product={product} asLink={Boolean(product.href)} />
              </div>
            ))}
          </div>

          {DIMENSIONS.map((dimension) => (
            <article
              className="comparison-dimension"
              data-dimension={dimension.id}
              data-tone={dimension.tone}
              key={dimension.id}
            >
              <header className="comparison-dimension-head">
                <div>
                  <p className="comparison-dimension-label">
                    <i aria-hidden="true" />
                    {dimension.label}
                  </p>
                  <h3>{dimension.claim}</h3>
                </div>
                <p className="comparison-dimension-boundary">{dimension.boundary}</p>
              </header>

              <div className="comparison-cells">
                {PRODUCTS.map((product) => {
                  const cell = dimension.cells[product.id];
                  return (
                    <div className="comparison-cell" data-product={product.id} key={product.id}>
                      <p className="comparison-cell-product">
                        <ProductName product={product} asLink={Boolean(product.href)} />
                      </p>
                      <p className="comparison-cell-lead">{cell.lead}</p>
                      <p className="comparison-cell-note">{cell.note}</p>
                      {"source" in cell ? (
                        <a
                          className="comparison-cell-source"
                          href={cell.source}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={`${product.name} ${dimension.label}依据`}
                        >
                          查看依据<ArrowUpRight size={12} aria-hidden="true" />
                        </a>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </article>
          ))}
        </div>

        <footer className="comparison-footer">
          <p className="comparison-source-note">
            公开资料核查于 <time dateTime={REVIEWED_ON}>{REVIEWED_ON}</time>
            。来源：
            {PRODUCTS.filter(
              (product): product is (typeof PRODUCTS)[number] & { href: string } =>
                typeof product.href === "string"
            ).map((product, index, listed) => (
              <span key={product.id}>
                {index > 0 ? (index === listed.length - 1 ? " 与 " : "、") : null}
                <a href={product.href} target="_blank" rel="noreferrer" aria-label={`${product.name} 官方资料`}>
                  {product.name}
                </a>
              </span>
            ))}
            。按所引文档的标准功能与接入方式比较；集成工作量为基于这些方式的判断，不含另行约定的私有化或定制交付。实际范围以对应版本及交付方案为准。
          </p>
          <div className="comparison-assurances">
            <p>
              <ShieldCheck size={18} aria-hidden="true" />
              <span>
                <strong>共享有边界</strong>
                成员和专家按授权访问资源。
              </span>
            </p>
            <p>
              <FileCheck2 size={18} aria-hidden="true" />
              <span>
                <strong>成果有依据</strong>
                保留引用与协作过程，便于核查和复用。
              </span>
            </p>
          </div>
        </footer>
      </div>
    </section>
  );
}
